import { add, Identify, identify, init, reset, setOptOut, setUserId, Types } from '@amplitude/analytics-browser'
import { inject, Injectable, InjectionToken, NgZone } from '@angular/core'
import { CourtSystem } from '@ftr/contracts/api/court-system'
import { Region } from '@ftr/contracts/type/court'
import { Uuid } from '@ftr/contracts/type/shared'

/**
 * Proof of concept for data sovereignty should this ever rollout to production.
 * Analytics in Amplitude are opted-in per court system region.
 */
const ALLOWED_REGIONS: Region[] = [Region['ap-southeast-2'], Region['us-west-2']]

export interface AmplitudeConfiguration {
  amplitudeApiKey: string | undefined
}

export const AMPLITUDE_CONFIGURATION = new InjectionToken<AmplitudeConfiguration>('AMPLITUDE_CONFIGURATION', {
  factory: () => ({ amplitudeApiKey: undefined }),
})

export interface AmplitudeUserProperties {
  userId: Uuid
  isAutomatedTestUser: boolean
  userEmailHostName: string
  userIsInDesktopApp: boolean
  isInternalFtrUser: boolean
}

type SessionReplayModule = typeof import('@amplitude/plugin-session-replay-browser')

/**
 * The plugin's entry point re-exports the standalone session replay SDK's options, which are a
 * different shape, so take the type from the factory that actually receives them.
 */
type SessionReplayOptions = NonNullable<Parameters<SessionReplayModule['sessionReplayPlugin']>[0]>

const ANALYTICS_OPTIONS: Types.BrowserOptions = {
  autocapture: true,
}

const SESSION_REPLAY_OPTIONS: SessionReplayOptions = {
  sampleRate: 1,
  privacyConfig: {
    // Amplitude will not ingest the text content of elements with `data-amp-mask`.
    // These elements are also visually redacted during session replay.
    maskSelector: ['[data-amp-mask]'],
  },
}

@Injectable({ providedIn: 'root' })
export class AmplitudeService {
  private readonly configuration = inject(AMPLITUDE_CONFIGURATION)
  private readonly ngZone = inject(NgZone)

  private isStarted = false
  private lastSentUser: string | undefined
  private lastSentCourtSystemId: Uuid | undefined

  setCourtSystem(courtSystem: CourtSystem): void {
    if (!ALLOWED_REGIONS.includes(courtSystem.region)) {
      this.optOut()
      return
    }

    this.start()

    if (!this.isStarted) {
      return
    }

    setOptOut(false)

    // Each identify call costs an event, and the store makes a new object for the same court system.
    if (courtSystem.id === this.lastSentCourtSystemId) {
      return
    }
    this.lastSentCourtSystemId = courtSystem.id

    identify(new Identify().set('courtSystemId', courtSystem.id))
  }

  clearCourtSystem(): void {
    this.optOut()
  }

  /**
   * One login dispatches several auth events (Authenticated, Login, LoginComplete), and each
   * identify call costs an event, so repeat calls with equal values do nothing.
   *
   * AnalyticsService registers the user before it reads the court system, so these calls usually
   * come in before the SDK starts. The SDK queues them and sends them at start.
   */
  setUser(user: AmplitudeUserProperties): void {
    const sentUser = JSON.stringify(user)
    if (sentUser === this.lastSentUser) {
      return
    }
    this.lastSentUser = sentUser

    setUserId(user.userId)
    identify(
      new Identify()
        .set('isAutomatedTestUser', user.isAutomatedTestUser)
        .set('userEmailHostName', user.userEmailHostName)
        .set('userIsInDesktopApp', user.userIsInDesktopApp)
        .set('isInternalFtrUser', user.isInternalFtrUser),
    )
  }

  /**
   * Removes personal data at logout: the reset clears the user id and makes a new anonymous
   * device id. It also empties the profile, so the next user needs the court system again.
   */
  clearUser(): void {
    this.lastSentUser = undefined
    this.lastSentCourtSystemId = undefined

    if (this.isStarted) {
      reset()
    }
  }

  /**
   * Starts the SDK outside the Angular zone. Amplitude listens for mousemove, scroll and click on
   * the document, and it observes each mutation of the body. In the Angular zone, every one of these
   * makes Angular run change detection, which writes to the DOM, which the observer reports as more
   * work, until the app stops responding.
   */
  private start(): void {
    const apiKey = this.configuration.amplitudeApiKey
    if (this.isStarted || !apiKey) {
      return
    }
    this.isStarted = true

    this.ngZone.runOutsideAngular(() => this.startSdk(apiKey))
  }

  private async startSdk(apiKey: string): Promise<void> {
    // Session replay is the bulk of the Amplitude code, so it is fetched on demand rather than
    // shipped in the initial bundle. Fetching it alongside init keeps it ready by the time init
    // resolves, so the plugin is in place before the first events go out.
    const sessionReplayModule = import('@amplitude/plugin-session-replay-browser')

    try {
      await init(apiKey, ANALYTICS_OPTIONS).promise

      const { sessionReplayPlugin } = await sessionReplayModule
      await add(sessionReplayPlugin(SESSION_REPLAY_OPTIONS)).promise
    } catch (error) {
      this.isStarted = false
      console.warn('Amplitude did not start. The app will not send analytics data.', error)
    }
  }

  private optOut(): void {
    if (this.isStarted) {
      setOptOut(true)
    }
  }
}
